import { isNil } from 'lodash';
import styled from 'styled-components';
import { UnreachableCaseError } from 'ts-essentials';
import {
  AnalysisStatus,
  IAnalysisContainerFieldsFragment,
  QueryType,
} from '../../../../utils/apollo/graphqlSchema';
import { globalFormat } from '../../../../utils/formatting';
import useBrowserTimezoneLabel from '../../../../utils/hooks/useBrowserTimezoneLabel';
import { MaybeNil } from '../../../../utils/interfaces';
import KubitTooltip from '../../../KubitTooltip';
import PermissionUserInfo from '../../../SharePermissionsWidget/PermissionUserInfo';
import { useChartContext } from '../../ChartContext';
import { ChartMode, ChartViewType } from '../../types';
import { theme } from '../../../../styles/theme';
import { assertCondition } from '../../../../utils/types';

const Container = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
`;

const CreateText = styled.div`
  margin-left: 2px;
  margin-right: 4px;
  color: ${theme.colors.secondary600};
`;

const getReportContainerName = (
  container: MaybeNil<IAnalysisContainerFieldsFragment>,
) => {
  if (isNil(container)) {
    return null;
  }
  switch (container.__typename) {
    case 'AnalysisDashboardContainer':
      return `dashboard ${container.name}`;
    case 'AnalysisWorkspaceContainer':
      return `workspace ${container.name}`;
    default:
      throw new UnreachableCaseError(container);
  }
};

const getTooltip = (executedAt: string) => {
  const { dateFormat, timeFormat } = globalFormat.getGlobalFormat();

  return globalFormat.formatDate(executedAt, {
    dateFormat,
    timeFormat,
  });
};

const ChartHeaderPermissions = () => {
  const browserTimeZoneLabel = useBrowserTimezoneLabel();
  const chartContext = useChartContext();
  assertCondition(chartContext.__typename === 'BaseChartContext');
  const {
    metadata: {
      chartView,
      mode,
      queryType,
      status,
      container,
      creator,
      executedAt,
    },
    permissions: { entityPermissions },
  } = chartContext;
  const showEntityPermissions =
    status === AnalysisStatus.Success &&
    chartView.type === ChartViewType.Default;

  const showEditBadge =
    queryType !== QueryType.CohortSample &&
    status === AnalysisStatus.Success &&
    chartView.type === ChartViewType.Default &&
    entityPermissions.editAnalysis &&
    mode === ChartMode.Build;

  const showViewOnlyBadge =
    queryType !== QueryType.CohortSample &&
    status === AnalysisStatus.Success &&
    chartView.type === ChartViewType.Default &&
    !entityPermissions.editAnalysis;

  const containerName = getReportContainerName(container);

  return (
    <Container>
      {showEditBadge && (
        <KubitTooltip
          placement="bottom-end"
          noOpenDelay={true}
          content={`You are editing a report${!isNil(containerName) ? ` in ${containerName}` : ''}`}
        >
          <div>You can edit •</div>
        </KubitTooltip>
      )}
      {showViewOnlyBadge && (
        <KubitTooltip
          placement="bottom-end"
          noOpenDelay={true}
          content="You have view-only access. To save changes, duplicate report."
        >
          <div>You can view •</div>
        </KubitTooltip>
      )}
      {showEntityPermissions && (
        <>
          <CreateText>Created by</CreateText>
          <PermissionUserInfo
            user={creator}
            contentLabel={'Created'}
            contentSuffix={`\n${getTooltip(executedAt)} ${browserTimeZoneLabel}`}
          />
        </>
      )}
    </Container>
  );
};

export default ChartHeaderPermissions;
